import { useAssignedMatches } from '@/pages/ChatPage/hooks/list/useAssignedMatches/useAssignedMatches'
import type { ChatRoomFilterInput } from '@/types/schema.generated'
import type { Ref } from 'vue'
import { computed, ref } from 'vue'
import { useMemberMatches } from './useMemberMatches/useMemberMatches'
import { useOwnMatches } from './useOwnMatches/useOwnMatches'

type Params = {
  activeView: Ref<Maybe<'own' | 'member' | 'assigned'>>
}

const INVALID_ROUTE_ERROR = new Error('invalid route error')

export const useChatList = (
  viewerId: string,
  entranceGUID: string,
  { activeView }: Params
) => {
  const filterInput = ref<ChatRoomFilterInput>({
    // すべて指定すること null は同義
    statuses: null,
    chatMessageStatuses: null,
    memoStatuses: null,
    lastMessageSentAt: null,
  })

  const {
    load: ownLoad,
    data: ownMatches,
    error: ownError,
    mutate: ownMutate,
    fetchMore: ownFetchMore,
    isLoadingNextPage: ownIsLoadingNextPage,
    hasNextPage: ownHasNextPage,
    onUpdateChatRoom: onUpdateOwnChatRoom,
  } = useOwnMatches(viewerId, entranceGUID, filterInput)
  const {
    load: memberLoad,
    data: memberMatches,
    error: memberError,
    mutate: memberMutate,
    fetchMore: memberFetchMore,
    isLoadingNextPage: memberIsLoadingNextPage,
    hasNextPage: memberHasNextPage,
  } = useMemberMatches()
  const {
    load: assignedLoad,
    data: assignedMatches,
    error: assignedError,
    mutate: assignedMutate,
    fetchMore: assignedFetchMore,
    isLoadingNextPage: assignedIsLoadingNextPage,
    hasNextPage: assignedHasNextPage,
    onUpdateChatRoom: onUpdateAssignedChatRoom,
  } = useAssignedMatches(viewerId, entranceGUID, filterInput)

  switch (activeView.value) {
    case 'own':
      ownLoad()
      break
    case 'member':
      memberLoad()
      break
    case 'assigned':
      assignedLoad()
      break
  }

  const hasNextPage = computed(() => {
    switch (activeView.value) {
      case 'own':
        return ownHasNextPage.value
      case 'member':
        return memberHasNextPage.value
      case 'assigned':
        return assignedHasNextPage.value
    }
    return false
  })

  const fetchNext = async () => {
    switch (activeView.value) {
      case 'own':
        await ownFetchMore()

        return
      case 'member':
        await memberFetchMore()

        return
      case 'assigned':
        await assignedFetchMore()

        return
    }
  }

  const isLoadingNext = computed(
    () =>
      (activeView.value === 'own' && ownIsLoadingNextPage.value) ||
      (activeView.value === 'member' && memberIsLoadingNextPage.value) ||
      (activeView.value === 'assigned' && assignedIsLoadingNextPage.value)
  )

  const data = computed(() => {
    switch (activeView.value) {
      case 'own':
        return ownMatches.value
      case 'member':
        return memberMatches.value
      case 'assigned':
        return assignedMatches.value
    }
    return undefined
  })

  const error = computed(() => {
    switch (activeView.value) {
      case 'own':
        return ownError.value
      case 'member':
        return memberError.value
      case 'assigned':
        return assignedError.value
    }
    return INVALID_ROUTE_ERROR
  })

  const mutate = computed(() => {
    switch (activeView.value) {
      case 'own':
        return ownMutate
      case 'member':
        return memberMutate
      case 'assigned':
        return assignedMutate
    }
    return () => {
      throw INVALID_ROUTE_ERROR
    }
  })

  const onUpdateChatRoom = (chatRoomIds: string[]) => {
    switch (activeView.value) {
      case 'own':
        onUpdateOwnChatRoom(chatRoomIds)
        return
      case 'assigned':
        onUpdateAssignedChatRoom(chatRoomIds)
        return
    }
  }

  return {
    data,
    error,
    mutate,
    fetchNext,
    hasNextPage,
    isLoadingNext,
    filterInput,
    onUpdateChatRoom,
  }
}
